import { useState } from 'react';
import type { SyncDomainSpec } from '../../identityxp/framework';
import { ProgressFeedback } from '../motionxp/ProgressFeedback';
import { Button } from '../ui/Button';
import { cn } from '../../lib/cn';

type Props = {
  domains: SyncDomainSpec[];
  onStart?: (selected: string[]) => void;
};

export function SyncProgressPanel({ domains, onStart }: Props) {
  const [selected, setSelected] = useState<string[]>(
    domains.filter((d) => d.default_selected).map((d) => d.domain),
  );
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [failed, setFailed] = useState(false);

  function toggle(domain: string) {
    setSelected((prev) =>
      prev.includes(domain) ? prev.filter((d) => d !== domain) : [...prev, domain],
    );
  }

  function startSync() {
    setRunning(true);
    setFailed(false);
    setProgress(0);
    onStart?.(selected);
    let p = 0;
    const interval = setInterval(() => {
      p += 12;
      setProgress(Math.min(p, 100));
      if (p >= 100) clearInterval(interval);
    }, 400);
  }

  function retry() {
    setFailed(false);
    startSync();
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-2 sm:grid-cols-2">
        {domains.map((d) => (
          <label
            key={d.domain}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm transition-colors',
              selected.includes(d.domain)
                ? 'border-brand-300 bg-brand-50 dark:border-brand-700 dark:bg-brand-950'
                : 'border-slate-200 dark:border-slate-700',
            )}
          >
            <input
              type="checkbox"
              checked={selected.includes(d.domain)}
              onChange={() => toggle(d.domain)}
              disabled={running}
              className="rounded"
            />
            <span className="flex-1 font-medium">{d.label}</span>
            {d.priority && (
              <span className="text-xs capitalize text-slate-500">{d.priority}</span>
            )}
          </label>
        ))}
      </div>

      {running && (
        <ProgressFeedback
          value={progress}
          label="Synchronizing selected domains"
          eta={progress < 100 ? '~2 minutes' : undefined}
        />
      )}

      {failed && (
        <p className="rounded-lg bg-error-50 p-3 text-sm text-error-700 dark:bg-error-950 dark:text-error-300">
          Sync interrupted. You can retry or resume from the last checkpoint.
        </p>
      )}

      <div className="flex gap-2">
        {!running && (
          <Button variant="primary" onClick={startSync} disabled={selected.length === 0}>
            Start synchronization
          </Button>
        )}
        {failed && (
          <Button variant="secondary" onClick={retry}>Retry</Button>
        )}
        {running && progress >= 100 && (
          <span className="flex items-center text-sm text-success-700 dark:text-success-300">✓ Sync complete</span>
        )}
      </div>
    </div>
  );
}
